import React from 'react';
interface FlipCardProps {
  flipped: boolean;
  onToggle: () => void;
  label: string;
  /**
   * Minimum height for the card. The card always grows to fit its taller
   * face, so content never scrolls inside it.
   */
  heightClassName?: string;
  /**
   * Increment to spin the card a full turn without changing the visible face.
   * Used when the card's content changes underneath the user.
   */
  spin?: number;
  children: {
    front: React.ReactNode;
    back: React.ReactNode;
  };
}

/**
 * A card that flips on click to reveal a second face. Clicks that land on a
 * nested control (button, link, input) are ignored so those stay usable.
 */
export function FlipCard({
  flipped,
  onToggle,
  label,
  heightClassName = 'min-h-[620px] lg:min-h-[min(720px,calc(100vh-16rem))]',
  spin = 0,
  children
}: FlipCardProps) {
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('button, a, input')) {
      return;
    }
    onToggle();
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle();
    }
  };
  return <div role="button" tabIndex={0} aria-pressed={flipped} aria-label={`${label}. ${flipped ? 'Showing pricing' : 'Click to show pricing'}.`} onClick={handleClick} onKeyDown={handleKeyDown} className={`relative grid w-full cursor-pointer [perspective:1600px] rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3] ${heightClassName}`} data-id="element-543" data-mp="1u7alav1ehr62">
      {/* Both faces share one grid cell, so the card is always as tall as its
          taller face — never a scrolling box. */}
      <div className="relative grid w-full transition-transform [transform-style:preserve-3d]" style={{
      transitionDuration: '390ms',
      transform: `rotateY(${(flipped ? 180 : 0) + spin * 360}deg)`,
      transitionTimingFunction: 'cubic-bezier(0.23, 1, 0.32, 1)'
    }} data-id="element-544" data-mp="11l97kz3jlhaq">
        <div aria-hidden={flipped} className="[grid-area:1/1] [backface-visibility:hidden] min-w-0" data-id="element-545" data-mp="131mrf4dm3i3n">
          {children.front}
        </div>
        <div aria-hidden={!flipped} className="[grid-area:1/1] [backface-visibility:hidden] [transform:rotateY(180deg)] min-w-0" data-id="element-546" data-mp="u0oybh4pouzu">
          {children.back}
        </div>
      </div>
    </div>;
}